import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { queryKeys } from "@/lib/query-keys";

export function useQueryInvalidation() {
  const queryClient = useQueryClient();

  const invalidateByKey = useCallback(
    (queryKey: readonly unknown[] | string | string[]) => {
      const keys = Array.isArray(queryKey) ? [queryKey] : [[queryKey]];

      keys.forEach((key) => {
        void queryClient.invalidateQueries({
          queryKey: key as readonly unknown[],
        });
      });
    },
    [queryClient],
  );

  const invalidateNotes = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.notes.home() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.todos.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.entities.all });
  }, [queryClient]);

  const invalidateTodos = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.todos.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
  }, [queryClient]);

  const invalidateEntities = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.entities.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
  }, [queryClient]);

  const invalidateNote = useCallback(
    (noteId: string) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notes.detail(noteId),
      });
      invalidateNotes();
    },
    [queryClient, invalidateNotes],
  );

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries();
  }, [queryClient]);

  const optimisticUpdateNote = useCallback(
    <T>(noteId: string, updater: (oldData: T | undefined) => T) => {
      queryClient.setQueryData(queryKeys.notes.detail(noteId), updater);
    },
    [queryClient],
  );

  const invalidateNoteLists = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.noteLists.all });
  }, [queryClient]);

  const invalidateNoteList = useCallback(
    (listId: string) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.noteLists.detail(listId),
      });
      invalidateNoteLists();
    },
    [queryClient, invalidateNoteLists],
  );

  return {
    invalidateByKey,
    invalidateNotes,
    invalidateTodos,
    invalidateEntities,
    invalidateNote,
    invalidateAll,
    optimisticUpdateNote,
    invalidateNoteLists,
    invalidateNoteList,
  };
}

export function useMutationWithInvalidation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    invalidateKeys?: (readonly unknown[])[];
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: TError, variables: TVariables) => void;
  },
) {
  const queryClient = useQueryClient();

  return {
    mutationFn,
    onSuccess: (data: TData, variables: TVariables) => {
      if (options?.invalidateKeys) {
        options.invalidateKeys.forEach((key) => {
          void queryClient.invalidateQueries({ queryKey: key });
        });
      }
      options?.onSuccess?.(data, variables);
    },
    onError: options?.onError,
  };
}
